/**
 * useChannelCompare · Module B 分渠道对比数据 Hooks
 *
 * 接口契约：
 * - 排名表 → POST /api/v1/marketing/ad_creative_multi/sum
 * - 柱状图 / 矩阵表 → POST /api/v1/marketing/ad_creative_multi/dimension_summary（新）
 * - 折线图 → POST /api/v1/marketing/ad_creative_multi/timeline（懒加载）
 *
 * 拆为三个独立 hook：
 *  - useChannelRanking  · 排名表（sum；前端分页 + 排序；need_link=true 带环比）
 *  - useChannelSummary  · 柱状图 / 矩阵表（dimension_summary；need_link=true 带环比；提供 barList / linkMap / linkTime / max）
 *  - useChannelTimeline · 折线图（timeline；由 wrapper 组件条件挂载实现"按需请求"）
 *
 * 渠道多选：业务层空数组表示"全部渠道"；接口层 `dimension_values=[]` 同义。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_V } from '@op/utils/format';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getSum, getTimeline, getDimensionSummary } from '../services';
import { IFilter } from '../types/filter';
import { TMetric, TSortOrder } from '../types/common';
import {
  IChannelChartBarItem,
  IChannelChartLineResponse,
  IChannelRankingItem,
} from '../types/channel';
import {
  ITimelineRawParams,
  ITimelineRawResponse,
  TTimelineMetric,
} from '../types/trend';
import { ISumParams, ISumResponse } from '../types/sum';
import {
  IDimensionSummaryParams,
  IDimensionSummaryResponse,
} from '../types/dimensionSummary';

// ─────────────────────────────────────────────────────────
// 公共：序列化数组为稳定 key（顺序无关）
// ─────────────────────────────────────────────────────────
function serializeIds(ids?: string[]): string {
  if (!ids || ids.length === 0) return '';
  return [...ids].sort().join(',');
}

// ─────────────────────────────────────────────────────────
// 排名表（sum 接口；前端分页 / 排序）
// 行=游戏，列=5 个 metric；与需求 §4.4.5 对齐
// ─────────────────────────────────────────────────────────

export interface IPageInfoState {
  page: number;
  page_size: number;
}

export interface ISortByState {
  by?: string;
  order?: TSortOrder;
}

export interface IUseChannelRankingArgs {
  filter: IFilter;
  channelIds: string[];
}

const DEFAULT_PAGE_INFO: IPageInfoState = { page: 1, page_size: 10 };

/**
 * 5 个 metric 一次拉全（避免 metric Segment 切换时重复请求）
 *
 * 用更窄的子集类型替代 `TSumMetric[]`，使下方 `row[metric]` / `row[link_${metric}]`
 * 索引访问能精确命中 `IChannelRankingItem` 的字段（避免 `engagement_stat` 等 sum
 * 接口 metric 误入循环）。
 */
type TChannelSumMetric =
  | 'creative_count_stat'
  | 'creative_count_stat_by_channel_percent'
  | 'creative_count_stat_by_competitor_percent'
  | 'creative_score_stat'
  | 'impression_stat';
const CHANNEL_SUM_METRICS: TChannelSumMetric[] = [
  'creative_count_stat',
  'creative_count_stat_by_channel_percent',
  'creative_count_stat_by_competitor_percent',
  'creative_score_stat',
  'impression_stat',
];

/** 环比时间窗口（来自 sum.link_time，hook 透传给上层供 RingRatioBubble 显示） */
export interface IChannelLinkTime {
  newTime: [string, string];
  oldTime: [string, string];
}

const EMPTY_LINK_TIME: IChannelLinkTime = {
  newTime: ['', ''],
  oldTime: ['', ''],
};

export function useChannelRanking({
  filter,
  channelIds,
}: IUseChannelRankingArgs) {
  const [list, setList] = useState<IChannelRankingItem[]>([]);
  const [linkTime, setLinkTime] = useState<IChannelLinkTime>(EMPTY_LINK_TIME);
  const [pageInfo, setPageInfo] = useState<IPageInfoState>(DEFAULT_PAGE_INFO);
  const [sortBy, setSortBy] = useState<ISortByState>({
    order: 'desc',
    by: 'creative_count_stat',
  });

  const { wrapperCancelRequest, loading } = useCancelRequestHook();
  const channelIdsKey = serializeIds(channelIds);

  /** 请求参数（filter / channelIds 变化才重发；排序 / 分页前端处理） */
  const params = useMemo<ISumParams | null>(() => {
    if (!filter?.start_time || !filter?.end_time || !filter?.game_ids?.length)
      return null;
    return {
      game_ids: filter.game_ids || [],
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
      metrics: CHANNEL_SUM_METRICS,
      dimension_values: channelIdsKey ? channelIdsKey.split(',') : [],
      need_link: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, channelIdsKey]);

  const request = useCallback(
    (nextParams: ISumParams) => {
      wrapperCancelRequest(getSum, nextParams)
        .then((response: any) => {
          if (!response) return;
          const games = (response.games || []) as ISumResponse['games'];
          // ⚠️ 按固定 metric 列表循环展平：当前值 + 环比值 + 环比变化率 三组字段
          // 环比变化率统一用 DBO_V.qoq（= getRationPercentValue）预算，cell 直接渲染
          const flat: IChannelRankingItem[] = games.map(g => {
            const row: Partial<IChannelRankingItem> = {
              game_id: g.game_id,
            };
            CHANNEL_SUM_METRICS.forEach(metric => {
              const cur = g.values?.[metric] ?? 0;
              const old = g.link_values?.[metric] ?? 0;
              row[metric] = cur;
              row[`link_${metric}`] = old;
              row[`qoq_${metric}`] = DBO_V.qoq(cur, old);
            });
            return row as IChannelRankingItem;
          });
          const lt = (response as ISumResponse).link_time;
          setLinkTime(
            lt
              ? {
                  newTime: [lt.new_start_date, lt.new_end_date],
                  oldTime: [lt.old_start_date, lt.old_end_date],
                }
              : EMPTY_LINK_TIME,
          );
          setList(flat);
        })
        .catch(() => {});
    },
    [wrapperCancelRequest],
  );

  useEffect(() => {
    if (!params) {
      setList([]);
      setLinkTime(EMPTY_LINK_TIME);
      return;
    }
    request(params);
  }, [params, request]);

  // 切 filter / 渠道 → 重置回第 1 页
  useEffect(() => {
    setPageInfo(DEFAULT_PAGE_INFO);
  }, [filter, channelIdsKey]);

  /** 前端排序后的全量 list */
  const sortedList = useMemo(() => {
    if (!sortBy.by) return list;
    const key = sortBy.by as keyof IChannelRankingItem;
    const dir = sortBy.order === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = (a[key] as number) ?? 0;
      const bv = (b[key] as number) ?? 0;
      return (av - bv) * dir;
    });
  }, [list, sortBy]);

  /** 前端切片到当前页 */
  const pagedList = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return sortedList.slice(start, start + pageInfo.page_size);
  }, [sortedList, pageInfo]);

  return {
    /** 当前页 list（表格用） */
    list: pagedList,
    /** 已排序的全量 list（download 用） */
    fullList: sortedList,
    /** 全量条数（前端分页 recordCount） */
    total: list.length,
    /** 环比时间窗口（RingRatioBubble 用；接口未返回时为空字符串数组） */
    linkTime,
    pageInfo,
    setPageInfo,
    sortBy,
    setSortBy,
    loading,
  };
}

// ─────────────────────────────────────────────────────────
// Timeline · 上方图表（line / bar / matrix 共用一次 API）
// ─────────────────────────────────────────────────────────

export interface IUseChannelTimelineArgs {
  filter: IFilter;
  channelIds: string[];
  metric: TMetric;
}

const EMPTY_TIMELINE: ITimelineRawResponse = {
  times: [],
  timeline_of_start_time: '',
  timeline_of_end_time: '',
  games: [],
};

/**
 * 前端 metric → timeline 接口 metric
 * 5 个 channel metric 在 timeline 接口里使用同名 metric 字段
 */
function mapToTimelineMetric(metric: TMetric): TTimelineMetric {
  return metric as TTimelineMetric;
}

/**
 * raw → 折线图 series
 * 把每个 (game, time) 在多个 dimension_value 上的值**累加**为单个 series 数据点
 * （多选渠道的折线 = 各渠道求和）
 */
function rawToLineSeries(
  raw: ITimelineRawResponse,
): IChannelChartLineResponse['series'] {
  const times = raw.times || [];
  return (raw.games || []).map(g => ({
    game_id: g.game_id,
    values: times.map((time, idx) => {
      const item = g.timeline?.[idx];
      const sum = (item?.values || []).reduce(
        (acc, v) => acc + (Number.isFinite(v.value) ? v.value : 0),
        0,
      );
      return { date: time, value: sum };
    }),
  }));
}

/**
 * useChannelTimeline · 折线图（timeline 接口）
 *
 * ⚠️ 本 hook 由 `ChannelLineChart` 组件内部直接调用，
 * 仅在 chartType === 'line' 时挂载 → 不需要 enabled 参数；
 * 组件卸载时 useCancelRequestHook 会自动取消 in-flight 请求。
 *
 * 仅返回折线图 series。柱状图 / 矩阵表的 barList 已迁移到 useChannelSummary。
 */
export function useChannelTimeline({
  filter,
  channelIds,
  metric,
}: IUseChannelTimelineArgs) {
  const [raw, setRaw] = useState<ITimelineRawResponse>(EMPTY_TIMELINE);
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setRaw(EMPTY_TIMELINE);
    },
  });
  const channelIdsKey = serializeIds(channelIds);

  const params = useMemo<ITimelineRawParams | null>(() => {
    if (!filter?.start_time || !filter?.end_time || !filter?.game_ids?.length)
      return null;
    return {
      game_ids: filter.game_ids || [],
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
      metric: mapToTimelineMetric(metric),
      dimension_values: channelIdsKey ? channelIdsKey.split(',') : [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, channelIdsKey, metric]);

  const request = useCallback(
    (nextParams: ITimelineRawParams) => {
      wrapperCancelRequest(getTimeline, nextParams)
        .then((response: any) => {
          if (!response) {
            return;
          }
          setRaw({
            times: response.times || [],
            timeline_of_start_time: response.timeline_of_start_time || '',
            timeline_of_end_time: response.timeline_of_end_time || '',
            games: response.games || [],
          });
          // setLoading(false);
        })
        .catch(() => {
          // setLoading(false);
        });
    },
    [wrapperCancelRequest],
  );

  useEffect(() => {
    if (!params) {
      setRaw(EMPTY_TIMELINE);
      // setLoading(false);
      return;
    }
    request(params);
  }, [params, request]);

  /** 折线图 series（按时间轴；多选渠道求和到一条） */
  const series = useMemo(() => rawToLineSeries(raw), [raw]);

  return { series, loading };
}

// ─────────────────────────────────────────────────────────
// Dimension Summary · 柱状图 / 矩阵表（dimension_summary 接口；need_link=true）
// ─────────────────────────────────────────────────────────

export interface IUseChannelSummaryArgs {
  filter: IFilter;
  channelIds: string[];
  metric: TMetric;
}

/** matrix cell 直接查的环比映射 */
export interface IChannelLinkCell {
  /** 当前周期值 */
  value: number;
  /** 环比期值 */
  oldValue: number;
  /** 环比变化率（百分比数值，12.34 → +12.34%） */
  qoq: number;
}

/** game_id → channel_id → cell（matrix 表内每格数据） */
export type TChannelLinkMap = Record<string, Record<string, IChannelLinkCell>>;

const EMPTY_SUMMARY: IDimensionSummaryResponse = { games: [] };

/**
 * useChannelSummary · 区间日均 + 环比（柱状图 / 矩阵表共用）
 *
 * 一次请求得到：
 *  - barList:      按 channel 分桶 [{ channel_id, games: [{ game_id, value }] }]
 *  - linkMap:      game_id → channel_id → { value, oldValue, qoq }
 *  - linkTime:     { newTime, oldTime }（hover 上期对比气泡用）
 *  - dimensionMax: channel_id → max（矩阵表 cell 进度条按列基准；接口未返回时前端按列兜底）
 *
 * dimension_value 顺序：渠道首次出现的顺序（与多选渠道一致；接口已按 value 降序）
 */
export function useChannelSummary({
  filter,
  channelIds,
  metric,
}: IUseChannelSummaryArgs) {
  const [data, setData] = useState<IDimensionSummaryResponse>(EMPTY_SUMMARY);
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setData(EMPTY_SUMMARY);
    },
  });
  const channelIdsKey = serializeIds(channelIds);

  /**
   * 矩阵表排序状态（提升至 hook → 下载时可复用，与表格展示完全一致）
   * - by:   channel_id；空表示未排序（默认数据顺序）
   * - order: 'asc' / 'desc'
   * 初始值由 barList 首个 channel 决定；后续仅由 UI 侧 setMatrixSort 主动变更
   */
  const [matrixSort, setMatrixSort] = useState<SortBy<any>>({
    by: '',
    order: 'desc',
  });
  const matrixInitializedRef = useRef(false);

  const params = useMemo<IDimensionSummaryParams | null>(() => {
    if (
      !filter?.start_time ||
      !filter?.end_time ||
      !metric ||
      !filter?.game_ids?.length
    )
      return null;
    return {
      game_ids: filter.game_ids || [],
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
      metric: mapToTimelineMetric(metric),
      dimension_values: channelIdsKey ? channelIdsKey.split(',') : [],
      need_link: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, channelIdsKey, metric]);

  const request = useCallback(
    (nextParams: IDimensionSummaryParams) => { 
      wrapperCancelRequest(getDimensionSummary, nextParams)
        .then((response: any) => {
          if (!response) { 
            return;
          }
          setData({
            games: response.games || [],
            link_time: response.link_time,
            dimension_max: response.dimension_max,
          }); 
        })
        .catch(() => { 
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [wrapperCancelRequest],
  );

  useEffect(() => {
    if (!params) {
      setData(EMPTY_SUMMARY); 
      return;
    }
    request(params);
  }, [params, request]);

  /**
   * 派生 barList：按 channel 分桶
   *
   * ⚠️ 全渠道 + 全游戏占位：
   * - channelIds 非空时作为权威渠道列表（铺满全部渠道，无数据的渠道填 0）
   * - channelIds 为空（全部渠道）时，以数据中出现的渠道为准
   * - 全部 game_id 以 `data.games` 顺序为权威列表（接口已严格按请求 game_ids
   *   返回，包括无数据的游戏 list = []），每个 channel 的 `games[]` 强制铺满
   *   全部 game_id，缺失的填 0。否则没有任何渠道数据的游戏会在矩阵表 transpose
   *   时直接消失（"返回 15 个但只展示 12 个"问题）。
   */
  const barList = useMemo<IChannelChartBarItem[]>(() => {
    const byChannel: Record<string, Record<string, number>> = {};
    const dataChannelOrder: string[] = [];
    (data.games || []).forEach(g => {
      (g.list || []).forEach(item => {
        const cid = item.dimension_value;
        if (!byChannel[cid]) {
          byChannel[cid] = {};
          dataChannelOrder.push(cid);
        }
        byChannel[cid][g.game_id] = Number.isFinite(item.value)
          ? item.value
          : 0;
      });
    });
    // 全部 game_id 列表（权威顺序 = 接口 games[] 顺序）
    const allGameIds = (data.games || []).map(g => g.game_id);
    // 渠道列表：优先使用外部传入的 channelIds（如果有指定），否则用数据中出现的渠道
    const allChannelIds =
      channelIds.length > 0
        ? channelIds.map(item => item.toLocaleLowerCase())
        : dataChannelOrder;

    return allChannelIds.map(cid => ({
      channel_id: cid,
      // 每列铺满全部游戏，缺失填 0（保证矩阵表 / 柱图都能看到全部游戏占位）
      games: allGameIds.map(gameId => ({
        game_id: gameId,
        value: byChannel[cid]?.[gameId] ?? 0,
      })),
    }));
  }, [data]);

  /** 派生 linkMap：game_id → channel_id → { value, oldValue, qoq } */
  const linkMap = useMemo<TChannelLinkMap>(() => {
    const map: TChannelLinkMap = {};
    (data.games || []).forEach(g => {
      const cur: Record<string, IChannelLinkCell> = {};
      const oldByDim: Record<string, number> = {};
      (g.link_list || []).forEach(item => {
        oldByDim[item.dimension_value] = Number.isFinite(item.value)
          ? item.value
          : 0;
      });
      (g.list || []).forEach(item => {
        const cid = item.dimension_value;
        const value = Number.isFinite(item.value) ? item.value : 0;
        const oldValue = oldByDim[cid] ?? 0;
        cur[cid] = {
          value,
          oldValue,
          qoq: DBO_V.qoq(value, oldValue),
        };
      });
      map[g.game_id] = cur;
    });
    return map;
  }, [data]);

  /** 环比时间窗口（hover 气泡用） */
  const linkTime = useMemo<IChannelLinkTime>(() => {
    const lt = data.link_time;
    return lt
      ? {
          newTime: [lt.new_start_date, lt.new_end_date],
          oldTime: [lt.old_start_date, lt.old_end_date],
        }
      : EMPTY_LINK_TIME;
  }, [data]);

  /**
   * 矩阵表 cell 进度条基准（按 channel 分别取 max）
   * - 优先用接口返回的 `dimension_max`
   * - 缺省时前端按 dimension_value 兜底（取 list 中各 dimension 自己的 max）
   */
  const dimensionMax = useMemo<Record<string, number>>(() => {
    if (data.dimension_max && Object.keys(data.dimension_max).length > 0) {
      return data.dimension_max;
    }
    const m: Record<string, number> = {};
    (data.games || []).forEach(g => {
      (g.list || []).forEach(item => {
        const v = Number.isFinite(item.value) ? item.value : 0;
        if (v > (m[item.dimension_value] ?? 0)) {
          m[item.dimension_value] = v;
        }
      });
    });
    return m;
  }, [data]);

  // 矩阵表排序 · 首次有数据时按首个 channel 初始化（仅一次，后续由 UI 控制）
  useEffect(() => {
    if (!matrixInitializedRef.current && barList?.length > 0) {
      const firstChannel = barList[0]?.channel_id;
      if (firstChannel) {
        setMatrixSort({ by: firstChannel, order: 'desc' });
        matrixInitializedRef.current = true;
      }
    }
  }, [barList]);

  return {
    barList,
    linkMap,
    linkTime,
    dimensionMax,
    loading,
    matrixSort,
    setMatrixSort,
  };
}
